import { useMemo } from 'react';

import { getUserSelectableCountries } from '@change-corgi/config/countries';

import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useCountryCode } from '#src/app/shared/hooks/l10n';
import type { StateWithoutError } from '#src/app/shared/utils/async';
import { isLoading } from '#src/app/shared/utils/async';

import { useCaptchaEnabled } from '../../useCaptchaEnabled';
import { useGeoFieldsEnabled } from '../useGeoFieldsEnabled';
import { useGeolocationValues } from '../useGeolocationValues';
import { usePublicInitialValues } from '../usePublicInitialValues';

// for easier testing in dev environment, uncomment following
// and replace hook's return value with "stubInitialValues";
// const stubInitialValues: Partial<FormValues> = {
// 	firstName: 'John',
// 	lastName: 'Doe',
// 	email: `jdoe+${Date.now()}@change.org`,
// 	countryCode: 'US',
// 	city: 'San Francisco',
// 	stateCode: 'CA',
// 	postalCode: '94016',
// };

type InitialValuesState = StateWithoutError<{ values: Partial<FormValues> }>;

export function useSignFormGuestInitialValues(): InitialValuesState {
	const countryCode = useCountryCode();
	const geoFieldsEnabled = useGeoFieldsEnabled();
	const geolocValues = useGeolocationValues();
	const captchaEnabled = useCaptchaEnabled();
	const publicInitialValues = usePublicInitialValues();

	const loadingState = useMemo(() => ({ status: 'loading' as const }), []);

	return useMemo(() => {
		if (isLoading(geolocValues) || isLoading(geoFieldsEnabled)) {
			return loadingState;
		}

		return {
			status: 'loaded',
			values: {
				countryCode: getUserSelectableCountries().includes(countryCode) ? countryCode : undefined,
				geoFieldsEnabled: geoFieldsEnabled.enabled,
				geolocPreview: !!geolocValues.values,
				captchaEnabled,
				...geolocValues.values,
				...publicInitialValues,
			},
		};
	}, [loadingState, countryCode, geoFieldsEnabled, geolocValues, captchaEnabled, publicInitialValues]);
}
